/**
 * The launchpad's tab strip: Home, one tab per open screen, and the open
 * editors as DRAFT tabs (dashed, with the + / pencil mark) in the same row —
 * so everything the user has in progress is one glance and one click away.
 *
 * WAI-ARIA tabs with MANUAL activation: arrow keys / Home / End move focus
 * between tabs, Enter / Space activate (opening a screen is not something a
 * focus move should do on its own). Delete closes the focused tab. Each close
 * × is a separate button; only the selected tab's × is in the Tab order, so
 * tabbing out of the strip is one stop, not one per tab.
 */
import { Home, X, XCircle } from 'lucide-react'
import { useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { tw, type TwHookProps } from '../../../testHooks'
import { EditorMark, UnsavedDot, editorTitle } from '../../nav/parts/EditorsList'
import { NavIcon } from '../../nav/navIcons'
import { S, iconButton } from '../../nav/tokens'
import type { RunNavItem } from '../../nav/types'
import type { ShellNavModel } from '../../nav/useShellNav'

export interface TabStripProps {
  model: ShellNavModel
  idBase: string
  homePanelId: string
  screenPanelId: string
  homeShown: boolean
  /** The open editor shown on the current screen (its draft tab is selected). */
  shownEditorId: string | null
  accent: string
  /** Briefly highlight the draft tabs ("Open editors" from the cap notice). */
  pulseDrafts: boolean
  onHome: () => void
  onScreen: (item: RunNavItem) => void
  onDraft: (id: string) => void
  stripRef: React.RefObject<HTMLDivElement | null>
}

export function tabId(idBase: string, key: string): string {
  return `${idBase}-tab-${key.replace(/[^A-Za-z0-9_-]/g, '_')}`
}

export function TabStrip({
  model, idBase, homePanelId, screenPanelId, homeShown, shownEditorId, accent, pulseDrafts,
  onHome, onScreen, onDraft, stripRef,
}: TabStripProps) {
  const closers = useRef(new Map<string, () => void>())
  closers.current.clear()
  const current = model.current

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const tabs = [...(stripRef.current?.querySelectorAll<HTMLElement>('[role="tab"]') || [])]
    const i = tabs.indexOf(document.activeElement as HTMLElement)
    if (i < 0) return
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1
      : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null
    if (next !== null) {
      e.preventDefault()
      const t = tabs[(next + tabs.length) % tabs.length]
      t.focus()
      t.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
      return
    }
    if (e.key === 'Delete') {
      const close = closers.current.get(tabs[i].dataset.tabKey || '')
      if (!close) return
      e.preventDefault()
      // Keep the keyboard in the strip: focus the neighbour before the tab goes.
      ;(tabs[i - 1] || tabs[i + 1])?.focus()
      close()
    }
  }

  const screenTabs = model.openScreens
  const drafts = model.editorsEnabled ? model.editors : []

  return (
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 6, minHeight: 50,
      padding: '6px 12px 0', background: S.panel, borderBottom: `1px solid ${S.border}`,
    }}>
      <div
        ref={stripRef}
        role="tablist"
        aria-label="Open screens and editors"
        onKeyDown={onKey}
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', gap: 4,
          overflowX: 'auto', overflowY: 'hidden', marginBottom: -1, scrollbarWidth: 'thin',
        }}
      >
        <TabShell selected={homeShown} accent={accent} style={{ minWidth: 0 }}>
          <button
            type="button"
            role="tab"
            id={tabId(idBase, 'home')}
            data-tab-key="home"
            aria-selected={homeShown}
            aria-controls={homePanelId}
            tabIndex={homeShown ? 0 : -1}
            className="tw-sh-btn"
            onClick={onHome}
            {...tw('action', 'shell-launchpad-home', { selected: homeShown })}
            style={tabButton(homeShown, { padding: '0 14px' })}
          >
            <Home size={17} aria-hidden />
            Home
          </button>
        </TabShell>

        {screenTabs.map((item) => {
          const selected = !homeShown && current?.id === item.id && !shownEditorId
          const key = `s:${item.id}`
          const close = () => model.closeScreen(item)
          closers.current.set(key, close)
          return (
            <TabShell key={key} selected={selected} accent={accent}>
              <button
                type="button"
                role="tab"
                id={tabId(idBase, key)}
                data-tab-key={key}
                aria-selected={selected}
                aria-controls={screenPanelId}
                aria-keyshortcuts="Delete"
                tabIndex={selected ? 0 : -1}
                className="tw-sh-btn"
                onClick={() => onScreen(item)}
                title={model.pathText(item) ? `${model.pathText(item)} › ${item.title}` : item.title}
                {...tw('action', 'shell-launchpad-tab', { obj: item.obj_name, type: item.obj_type, selected })}
                style={tabButton(selected)}
              >
                <span aria-hidden style={tabIconBox}>
                  <NavIcon node={item} size={15} />
                </span>
                <span className="tw-sh-clip" style={{ minWidth: 0 }}>{item.title}</span>
              </button>
              <CloseButton
                label={`Close ${item.title}`}
                tabbable={selected}
                onClick={close}
                hooks={tw('action', 'shell-launchpad-tab-close', { obj: item.obj_name })}
              />
            </TabShell>
          )
        })}

        {drafts.map((s) => {
          const selected = !homeShown && shownEditorId === s.id
          const key = `e:${s.id}`
          const title = editorTitle(s)
          const screen = model.itemFor(s.objName)?.title || s.objName
          const close = () => model.closeEditor(s.id)
          closers.current.set(key, close)
          return (
            <TabShell key={key} selected={selected} accent={accent} draft pulse={pulseDrafts}>
              <button
                type="button"
                role="tab"
                id={tabId(idBase, key)}
                data-tab-key={key}
                data-draft-tab=""
                aria-selected={selected}
                aria-controls={screenPanelId}
                aria-keyshortcuts="Delete"
                aria-label={`${s.mode === 'add' ? 'New' : 'Editing'} ${title}, ${screen}${s.dirty ? ', unsaved changes' : ''}`}
                tabIndex={selected ? 0 : -1}
                className="tw-sh-btn"
                onClick={() => onDraft(s.id)}
                title={`${title} — ${screen}`}
                {...tw('action', 'shell-launchpad-draft', { obj: s.objName, mode: s.mode, txn: s.txnId, dirty: s.dirty, selected })}
                style={tabButton(selected, { fontStyle: 'italic' })}
              >
                <EditorMark session={s} model={model} size={26} />
                <span className="tw-sh-clip" style={{ minWidth: 0 }}>{title}</span>
                {s.dirty && <UnsavedDot />}
              </button>
              <CloseButton
                label={`Close editor ${title}`}
                tabbable={selected}
                onClick={close}
                hooks={tw('action', 'shell-launchpad-draft-close', { obj: s.objName, txn: s.txnId })}
              />
            </TabShell>
          )
        })}
      </div>
      {screenTabs.length > 1 && (
        <button
          type="button"
          className="tw-sh-btn"
          onClick={model.closeAllScreens}
          aria-label="Close all screen tabs"
          title="Close all screen tabs"
          {...tw('action', 'shell-launchpad-close-all')}
          style={{ ...iconButton(36), marginBottom: 6 }}
        >
          <XCircle size={17} aria-hidden />
        </button>
      )}
    </div>
  )
}

/** One tab's frame: a folder-tab top that, when selected, takes the content's
 *  ground colour and covers the strip's bottom rule so it joins the page. */
function TabShell({ selected, accent, draft = false, pulse = false, style, children }: {
  selected: boolean; accent: string; draft?: boolean; pulse?: boolean; style?: CSSProperties; children: ReactNode
}) {
  const borderColor = selected ? (draft ? accent : S.border) : (draft ? S.borderStrong : 'transparent')
  return (
    <div style={{
      position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center',
      height: 44, minWidth: 140, maxWidth: 240, padding: '0 4px 0 4px', boxSizing: 'border-box',
      borderRadius: '10px 10px 0 0',
      border: `1px ${draft ? 'dashed' : 'solid'} ${borderColor}`,
      borderBottom: selected ? `1px solid ${S.bg}` : `1px solid ${draft ? S.border : 'transparent'}`,
      background: selected ? S.bg : 'transparent',
      outline: pulse && draft ? `2px solid ${accent}` : 'none',
      outlineOffset: -3,
      ...style,
    }}>
      {/* Selected indicator that does not rely on the fill alone. */}
      {selected && (
        <span aria-hidden style={{
          position: 'absolute', left: 8, right: 8, top: -1, height: 3, borderRadius: '0 0 3px 3px',
          background: accent,
        }} />
      )}
      {children}
    </div>
  )
}

function CloseButton({ label, tabbable, onClick, hooks }: {
  label: string; tabbable: boolean; onClick: () => void; hooks: TwHookProps
}) {
  return (
    <button
      type="button"
      className="tw-sh-btn"
      onClick={onClick}
      aria-label={label}
      title={label}
      tabIndex={tabbable ? 0 : -1}
      {...hooks}
      style={{ ...iconButton(30), borderRadius: 7 }}
    >
      <X size={14} aria-hidden />
    </button>
  )
}

const tabIconBox: CSSProperties = {
  width: 26, height: 26, flexShrink: 0, borderRadius: 7, background: S.accentWeak, color: S.accent,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
}

function tabButton(selected: boolean, extra?: CSSProperties): CSSProperties {
  return {
    flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 8, height: 38,
    padding: '0 6px 0 6px', border: 'none', borderRadius: 8, background: 'transparent',
    color: selected ? S.text : S.text2, fontSize: 14, fontWeight: selected ? 600 : 500,
    fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
    ...extra,
  }
}
